import React, { useState, useCallback, useMemo } from 'react';
import { Bubble, Sender } from '@ant-design/x';
import { Card, Typography, Tag, Alert, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import 'antd/dist/reset.css';

// 错误边界组件
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          message="出现错误"
          description="应用程序遇到了一个错误，请刷新页面重试。"
          type="error"
          showIcon
          style={{ margin: 20 }}
        />
      );
    }

    return this.props.children;
  }
}

const { Title, Text, Paragraph } = Typography;

// 使用 React.memo 优化搜索结果项组件，避免不必要的重渲染
const SearchResultItem = React.memo(({ item }) => {
  // 提取文件名，避免重复计算
  const fileName = useMemo(() => {
    if (!item.meta?.source) return '';
    return item.meta.source.split('/').pop();
  }, [item.meta?.source]);
  
  // 格式化相关度分数
  const scorePercentage = useMemo(() => {
    return (item.score * 100).toFixed(1);
  }, [item.score]);

  return (
    <Card 
      size="small" 
      style={{ marginBottom: 16 }}
      title={
        <div>
          <Text strong>{item.title}</Text>
          <Tag color="blue" style={{ marginLeft: 8 }}>
            相关度: {scorePercentage}%
          </Tag>
        </div>
      }
    >
      <Paragraph ellipsis={{ rows: 3, expandable: true }}>
        {item.snippet}
      </Paragraph>
      {item.meta && (
        <div style={{ marginTop: 8 }}>
          {item.meta.page_label && (
            <Text type="secondary">页码: {item.meta.page_label}</Text>
          )}
          {fileName && (
            <Text type="secondary" style={{ marginLeft: 16 }}>
              来源: {fileName}
            </Text>
          )}
        </div>
      )}
    </Card>
  );
});

// 使用 React.memo 优化搜索结果列表组件
const SearchResults = React.memo(({ responses }) => {
  // 使用 useMemo 缓存渲染的搜索结果项
  const renderedItems = useMemo(() => {
    if (!responses || responses.length === 0) return [];
    return responses.map((item, index) => (
      <SearchResultItem key={index} item={item} />
    ));
  }, [responses]);
  
  if (!responses || responses.length === 0) return null;
  
  return (
    <div style={{ marginTop: 16 }}>
      <Title level={4}>搜索结果详情</Title>
      {renderedItems}
    </div>
  );
});



// 初始欢迎消息
const WELCOME_MESSAGE = {
  content: '你好！我是 昔阳AI 对话助手，有什么可以帮助你的吗？',
  role: 'assistant',
  key: 'assistant_0',
};

// API 端点 - 必须使用完整地址
const API_ENDPOINT = 'http://mcpsse.shangchat.cn/chat';

const App = () => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [loading, setLoading] = useState(false);

  // 使用 useCallback 缓存 API 调用函数
  const callApi = useCallback(async (message) => {
    try {
      setLoading(true);
      
      // 尝试直接请求
      try {
        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          mode: 'cors', // 启用跨域请求
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Origin': window.location.origin,
          },
          body: JSON.stringify({ message }),
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return await response.json();
      } catch (directError) {
        console.warn('直接请求失败:', directError.message);
        
        // 尝试使用代理方式
        try {
          console.log('尝试使用代理方式解决跨域问题');
          const proxyResponse = await fetch('/api/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ message }),
          });
          
          if (!proxyResponse.ok) {
            throw new Error(`Proxy error! status: ${proxyResponse.status}`);
          }
          
          return await proxyResponse.json();
        } catch (proxyError) {
          console.warn('代理请求也失败:', proxyError.message);
          throw new Error('无法连接到服务器，请检查网络连接或稍后再试。');
        }
      }
    } catch (error) {
      console.error('API调用失败:', error);
      return { error: '无法连接到服务器，请检查网络连接或稍后再试。' };
    } finally {
      setLoading(false);
    }
  }, []);

  // 使用 useCallback 缓存响应处理函数
  const processResponse = useCallback((response) => {
    if (response.error) {
      return { content: response.error, searchResults: null, shouldRetry: false };
    }
    
    if (typeof response === 'string') {
      // 检查是否包含错误消息
      if (response.includes('Attempted to exit cancel scope in a different task than it was entered in')) {
        return { content: '', searchResults: null, shouldRetry: true };
      }
      return { content: response, searchResults: null, shouldRetry: false };
    }
    
    if (response.ok && response.answer) {
      // 检查答案是否包含错误消息
      if (response.answer.includes('Attempted to exit cancel scope in a different task than it was entered in')) {
        return { content: '', searchResults: null, shouldRetry: true };
      }
      return {
        content: response.answer,
        searchResults: response.responses?.length > 0 ? response.responses : null,
        shouldRetry: false
      };
    }
    
    if (response.message) {
      // 检查消息是否包含错误消息
      if (response.message.includes('Attempted to exit cancel scope in a different task than it was entered in')) {
        return { content: '', searchResults: null, shouldRetry: true };
      }
      return { content: response.message, searchResults: null, shouldRetry: false };
    }
    
    if (response.data) {
      const content = typeof response.data === 'string' 
        ? response.data 
        : JSON.stringify(response.data);
      
      // 检查数据是否包含错误消息
      if (content.includes('Attempted to exit cancel scope in a different task than it was entered in')) {
        return { content: '', searchResults: null, shouldRetry: true };
      }
      return { content, searchResults: null, shouldRetry: false };
    }
    
    const jsonContent = JSON.stringify(response);
    // 检查JSON内容是否包含错误消息
    if (jsonContent.includes('Attempted to exit cancel scope in a different task than it was entered in')) {
      return { content: '', searchResults: null, shouldRetry: true };
    }
    
    return { content: jsonContent, searchResults: null, shouldRetry: false };
  }, []);

  // 使用 useCallback 缓存发送消息处理函数
  const handleSend = useCallback(async (content, retryCount = 0) => {
    if (!content.trim() || loading) return;
    
    const timestamp = Date.now();
    
    // 创建用户消息（仅在第一次调用时添加）
    let userMessage;
    if (retryCount === 0) {
      userMessage = {
        content,
        role: 'user',
        key: `user_${timestamp}`,
      };
    }
    
    // 创建加载消息
    const loadingMessage = {
      content: retryCount > 0 ? '正在重新请求...' : '正在思考中...',
      role: 'assistant',
      key: `assistant_loading_${timestamp}_${retryCount}`,
      loading: true,
    };
    
    // 添加新消息
    if (retryCount === 0) {
      setMessages(prev => [...prev, userMessage, loadingMessage]);
    } else {
      setMessages(prev => [...prev, loadingMessage]);
    }
    
    try {
      // 调用API
      const response = await callApi(content);
      
      // 处理响应
      const { content: assistantContent, searchResults, shouldRetry } = processResponse(response);
      
      // 如果需要重试且重试次数未超过限制
      if (shouldRetry && retryCount < 3) {
        // 更新加载消息为重试提示
        const retryMessage = {
          content: `检测到错误，正在重试 (${retryCount + 1}/3)...`,
          role: 'assistant',
          key: `assistant_retry_${timestamp}_${retryCount}`,
        };
        
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.key === loadingMessage.key ? retryMessage : msg
          )
        );
        
        // 延迟后重试
        setTimeout(() => {
          handleSend(content, retryCount + 1);
        }, 1000);
        
        return;
      }
      
      // 创建助手消息
      const assistantMessage = {
        content: assistantContent || (shouldRetry ? '抱歉，多次重试后仍无法获取有效响应，请稍后再试。' : ''),
        role: 'assistant',
        key: `assistant_${timestamp + 1}_${retryCount}`,
        searchResults,
      };
      
      // 替换加载消息
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.key === loadingMessage.key ? assistantMessage : msg
        )
      );
    } catch (error) {
      console.error('处理消息时出错:', error);
      
      // 创建错误消息
      const errorMessage = {
        content: '处理消息时出错，请重试。',
        role: 'assistant',
        key: `assistant_error_${timestamp + 1}_${retryCount}`,
      };
      
      // 替换加载消息
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.key === loadingMessage.key ? errorMessage : msg
        )
      );
    }
  }, [callApi, processResponse, loading]);

  // 使用 useCallback 缓存消息渲染函数
  const renderMessage = useCallback((item) => {
    if (item.role === 'assistant' && item.searchResults) {
      return (
        <div>
          <div>{item.content}</div>
          <SearchResults responses={item.searchResults} />
        </div>
      );
    }
    return <div>{item.content}</div>;
  }, []);

  // 使用 useMemo 缓存样式对象
  const containerStyle = useMemo(() => ({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
  }), []);

  const chatContainerStyle = useMemo(() => ({
    flex: 1,
    overflowY: 'auto',
    marginBottom: '20px',
  }), []);

  return (
    <div style={containerStyle}>
      <h1 style={{ textAlign: 'center', marginBottom: '20px' }}>昔阳对话助手</h1>
      <div style={chatContainerStyle}>
        <Bubble.List 
          items={messages} 
          renderMessage={renderMessage}
        />
      </div>
      <Sender onSubmit={handleSend} loading={loading} />
    </div>
  );
};

// 使用 ConfigProvider 包裹整个应用，提供中文语言支持
export default () => (
  <ConfigProvider locale={zhCN}>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </ConfigProvider>
);